import { z } from "zod";

import { isDevelopment } from "@av/build-constants";
import type { FormExternalError } from "@av/components/atoms/form/types";
import { getDisplayedError } from "./api-config/error-i18n-mappings";
import { type ApiErrorResponse, ValidationErrorSchema } from "../admin-settings/type";

import type { AxiosError } from "axios";
import type { TFunction } from "i18next";

/**
 * Maps API validation errors from an Axios error response to form server errors.
 *
 * @param axiosError - The {@link AxiosError} containing {@link ApiErrorResponse} with validation errors from the API
 * @returns Array of {@link FormExternalError} ready for display in form fields
 */
export function mapErrors(
  t: TFunction,
  axiosError?: AxiosError<ApiErrorResponse> | null
): FormExternalError[] {
  if (axiosError == null) {
    return [];
  }

  const validationErrors = axiosError?.response?.data?.validationErrors;

  if (!validationErrors) {
    return [];
  }

  const typeCheck = z.array(ValidationErrorSchema).safeParse(validationErrors);

  if (!typeCheck.success) {
    if (isDevelopment) {
      console.warn("Server returned an unrecognized error object: ", axiosError?.response?.data, {
        typeCheck,
      });

      throw Error("Server returned an unrecognized validationErrors object");
    }

    return [];
  }

  const serverErrors = validationErrors
    .map((e) => ({
      field: e.field,
      error: getDisplayedError(t, e.code, e.params),
    }))
    .sort(
      (a, b) =>
        (a.error.priority ?? Number.MAX_SAFE_INTEGER) -
        (b.error.priority ?? Number.MAX_SAFE_INTEGER)
    )
    .filter((err, index, arr) => arr.findIndex((e) => e.field === err.field) === index);

  return serverErrors;
}
